import { Box, CircularProgress, Stack, Typography } from "@mui/joy";
import { ModalBackdrop } from "@mui/joy/Modal/Modal";
import { collection, getDocs, orderBy, query } from "firebase/firestore";
import React from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate } from "react-router";
import { collectionData } from "rxfire/firestore";
import { auth, db, onAuthStateChanged } from "../config/firebase";
import { fetchCryptoPrices, getCachedPrices } from "../config/prices";
import { getNotification, getUserInfo, toCoinAmount } from "../config/services";
import {
  notification$,
  notificationall$,
  userinfo$,
  walletData$,
  walletInfos$,
} from "../redux/action";

const buildWalletArray = (userData, prices, addresses) => {
  const {
    btc_balance,
    bnb_balance,
    tron_balance,
    eth_balance,
    usdt_balance,
    usdterc20_balance,
  } = userData;
  const { btc, bnb, eth, trx, usdt_trc20, usdt_erc20 } = addresses;

  const myArray = [
    {
      coinname: "BITCOIN",
      cointype: "btc_balance",
      price: prices.bitcoin.usd,
      difference: prices.bitcoin.usd_24h_change,
      balance: btc_balance,
      balancecoin: toCoinAmount(btc_balance, prices.bitcoin.usd),
      code: "BTC",
      codeswap: "BTC",
      image: "./images/coins/btc.png",
      address: btc,
      network: "BTC",
      thumbimage: "./images/coins/btc.png",
      contractaddress: false,
    },
    {
      coinname: "ETHERUEM",
      cointype: "eth_balance",
      price: prices.ethereum.usd,
      difference: prices.ethereum.usd_24h_change,
      balance: eth_balance,
      balancecoin: toCoinAmount(eth_balance, prices.ethereum.usd),
      code: "ETH",
      codeswap: "ETH",
      image: "./images/coins/eth.png",
      address: eth,
      network: "ERC20",
      thumbimage: "./images/coins/eth.png",
      contractaddress: false,
    },
    {
      coinname: "BNB SMART",
      cointype: "bnb_balance",
      price: prices.binancecoin.usd,
      difference: prices.binancecoin.usd_24h_change,
      balance: bnb_balance,
      balancecoin: toCoinAmount(bnb_balance, prices.binancecoin.usd),
      code: "BNB",
      codeswap: "BNB",
      image: "./images/coins/bnb.png",
      address: bnb,
      network: "BEP20",
      thumbimage: "./images/coins/bnb.png",
      contractaddress: false,
    },
    {
      coinname: "TRON",
      cointype: "tron_balance",
      price: prices.tron.usd,
      difference: prices.tron.usd_24h_change,
      balance: tron_balance,
      balancecoin: toCoinAmount(tron_balance, prices.tron.usd),
      code: "TRX",
      codeswap: "TRX",
      image: "./images/coins/tron.png",
      address: trx,
      network: "TRC20",
      thumbimage: "./images/coins/tron.png",
      contractaddress: false,
    },
    {
      coinname: "USDT",
      cointype: "usdt_balance",
      price: prices.tether.usd,
      difference: prices.tether.usd_24h_change,
      balance: usdt_balance,
      balancecoin: toCoinAmount(usdt_balance, prices.tether.usd),
      code: "USDT TRC20",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: usdt_trc20,
      network: "TRC20",
      thumbimage: "./images/coins/tron.png",
      contractaddress: true,
    },
    {
      coinname: "USDT",
      cointype: "usdterc20_balance",
      price: prices.tether.usd,
      difference: prices.tether.usd_24h_change,
      balance: usdterc20_balance,
      balancecoin: toCoinAmount(usdterc20_balance, prices.tether.usd),
      code: "USDT ERC20",
      codeswap: "USDT",
      image: "./images/coins/usdt.png",
      address: usdt_erc20,
      network: "ERC20",
      thumbimage: "./images/coins/eth.png",
      contractaddress: true,
    },
  ];

  myArray.sort((a, b) => b.balance - a.balance);
  return myArray;
};

export function DashboardIndex() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const dispatch = useDispatch();

  const pricesRef = React.useRef(getCachedPrices());
  const userDataRef = React.useRef(null);
  const addressesRef = React.useRef(null);
  const addressIndexRef = React.useRef(undefined);

  const rebuildWallet = React.useCallback(() => {
    const userData = userDataRef.current;
    const prices = pricesRef.current;
    const addresses = addressesRef.current;
    if (!userData || !prices || !addresses) return;

    dispatch(walletData$(buildWalletArray(userData, prices, addresses)));
    setLoading(false);
  }, [dispatch]);

  React.useEffect(() => {
    let userSub = null;
    let notifications = null;
    let cancelled = false;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/auth/login");
        return;
      }

      getNotification(user.uid);

      // Prices: fetch once (cached / deduped), independent of Firestore updates
      fetchCryptoPrices()
        .then((prices) => {
          if (cancelled) return;
          pricesRef.current = prices;
          rebuildWallet();
        })
        .catch((error) => {
          console.log(error);
          const fallback = getCachedPrices();
          if (fallback && !cancelled) {
            pricesRef.current = fallback;
            rebuildWallet();
          } else if (!cancelled) {
            setLoading(false);
          }
        });

      // User balances: rebuild wallet from cached prices; do not re-hit CoinGecko
      userSub = getUserInfo(user.uid).subscribe((userData) => {
        const {
          btc_balance,
          bnb_balance,
          tron_balance,
          eth_balance,
          usdt_balance,
          usdterc20_balance,
          addressindex,
        } = userData;

        const totalbalance = [
          btc_balance,
          bnb_balance,
          tron_balance,
          eth_balance,
          usdt_balance,
          usdterc20_balance,
        ].reduce((prv, cur) => prv + cur, 0);

        dispatch(
          userinfo$({ ...userData, id: user.uid, totalBalance: totalbalance })
        );

        userDataRef.current = userData;

        const addressIndexChanged = addressIndexRef.current !== addressindex;
        if (!addressesRef.current || addressIndexChanged) {
          addressIndexRef.current = addressindex;
          getDocs(collection(db, "walletaddresses")).then((querySnapshot) => {
            if (cancelled) return;
            const data =
              addressindex === undefined
                ? querySnapshot.docs[0].data()
                : querySnapshot.docs[addressindex].data();
            addressesRef.current = data;
            dispatch(walletInfos$(data));
            rebuildWallet();
          });
        } else {
          rebuildWallet();
        }
      });

      const usertransactionref = query(
        collection(db, "users", user.uid, "transactions"),
        orderBy("timestamp", "desc")
      );
      notifications = collectionData(usertransactionref).subscribe((data) => {
        const getAllDocsWithoutField = (fieldName) =>
          data.filter((docSnap) => !(fieldName in docSnap));
        dispatch(notificationall$(data.length));
        dispatch(notification$(getAllDocsWithoutField("seen").length));
      });
    });

    return () => {
      cancelled = true;
      unsubAuth();
      if (userSub) userSub.unsubscribe();
      if (notifications) notifications.unsubscribe();
    };
  }, [dispatch, navigate, rebuildWallet]);

  return (
    <>
      <Helmet>
        <script src="https://widgets.coingecko.com/gecko-coin-price-marquee-widget.js"></script>
        <script src="//code.jivosite.com/widget/v78WXs0Awp" async></script>
      </Helmet>
      {loading ? (
        <ModalBackdrop
          sx={{
            flexDirection: "column",
            backgroundColor: (theme) => theme.palette.background.paper,
            zIndex: 1000,
          }}
        >
          <Box
            mt={1}
            display={"flex"}
            justifyContent={"center"}
            position={"relative"}
            top={"40%"}
          >
            <Stack spacing={1} alignItems={"center "}>
              <CircularProgress color="primary" />
              <Typography level="body-lg">Loading...</Typography>
            </Stack>
          </Box>
        </ModalBackdrop>
      ) : (
        <Outlet />
      )}
    </>
  );
}

/*
 <Helmet>
      <script src="//code.tidio.co/y4qvyvglyyragn1zlquny8um9fpsgoyg.js" async></script>
      </Helmet>

      <Backdrop open={loading}>
        <CustomCirleLoader />
      </Backdrop>
*/
